import { useMemo } from 'react';
import { GetReservation200 } from '../../openapi/model';
import { useGetTicketOptions } from '../../openapi/ticket-options/ticket-options';
import { useGetTicketTypes } from '../../openapi/ticket-types/ticket-types';
import { useLang } from '../hooks/useLanguage';

function ReservationTickets({
    reservation,
    // the pending order is needed so we can tell if a ticket is "about to be deleted" or "will be rolled back"
    pendingOrder,
}: {
    reservation: GetReservation200;
    pendingOrder: any;
}) {
    const { getI18n, t } = useLang();

    const { data: ticketOptions, isLoading: isTicketOptionsLoading } = useGetTicketOptions({
        optionalId: reservation.showId as unknown as number,
    });
    const { data: ticketTypes, isLoading: isTicketTypesLoading } = useGetTicketTypes({
        optionalId: reservation.showId as unknown as number,
        includeSeats: true,
        onlyShowInList: false,
    });
    const seats = reservation.cart;

    // Create a merged ticket data structure that includes ticket type and option names and quantities
    const mergedTicketData = useMemo(() => {
        if (!ticketOptions || !ticketTypes) {
            return [];
        }

        const extendedSeats = seats.map((ticket) => {
            const ticketType = ticketTypes.ticketTypes.find(
                (ticketType) => ticketType.id === ticket.ticketTypeId,
            );
            const ticketOption = ticketOptions.ticketOptions.find(
                (ticketOption) => ticketOption.id === ticket.ticketOptionId,
            );
            return {
                ...ticket,
                ticketType,
                ticketOption,
                isPendingForAdding: Array.isArray(pendingOrder?.ticketsAdded)
                    ? pendingOrder.ticketsAdded.includes(ticket.id)
                    : false,
                isPendingForRemoving: Array.isArray(pendingOrder?.ticketsRemoved)
                    ? pendingOrder.ticketsRemoved.includes(ticket.id)
                    : false,
            };
        });

        // group by isPendingForAdding, isPendingForRemoving, ticket.ticketOptionId, ticket.ticketTypeId, ticket.price
        // Group tickets by option/type/price AND pending flags, so tickets with different pending states are not merged
        const groupedTickets = extendedSeats.reduce<
            Record<string, (typeof extendedSeats)[0] & { quantity: number }>
        >((acc, ticket) => {
            // Include pending flags in the key so tickets with different pending states are not grouped together
            const key = `${ticket.ticketOptionId}-${ticket.ticketTypeId}-${ticket.price}-${ticket.isPendingForAdding}-${ticket.isPendingForRemoving}`;
            if (!acc[key]) {
                acc[key] = {
                    ...ticket,
                    quantity: 0,
                    isPendingForAdding: ticket.isPendingForAdding,
                    isPendingForRemoving: ticket.isPendingForRemoving,
                };
            }
            acc[key].quantity += 1;
            return acc;
        }, {});
        // convert back to array
        return Object.values(groupedTickets);
    }, [seats, ticketTypes, ticketOptions, pendingOrder]);

    if (isTicketOptionsLoading || isTicketTypesLoading) {
        return null;
    }

    return (
        <div className="fireknife-booking__tickets">
            <h2 className="fireknife-booking__tickets-title">{t('widgetSidebarSteps.tickets')}</h2>

            <table className="fireknife-order-data__tickets-table">
                <thead>
                    <tr>
                        <th className="fireknife-text-start">
                            {t('widgetBookingSummary.tableColumns.quantity')}
                        </th>
                        <th className="fireknife-text-start fireknife-order-data__tickets-table-largecolumn">
                            {t('widgetBookingSummary.tableColumns.name')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {mergedTicketData.map((ticket, index) => {
                        return (
                            <tr key={index}>
                                <td
                                    className="fireknife-text-start"
                                    aria-label={t('widgetBookingSummary.tableColumns.quantity')}
                                >
                                    {ticket.quantity}
                                </td>
                                <td
                                    className="fireknife-text-start"
                                    aria-label={t('widgetBookingSummary.tableColumns.name')}
                                >
                                    {getI18n(
                                        ticket?.ticketType?.name,
                                        ticket?.ticketType?.nameI18n,
                                    )}{' '}
                                    {getI18n(
                                        ticket?.ticketOption?.name,
                                        ticket?.ticketOption?.nameI18n,
                                    )}
                                    {ticket.isPendingForAdding && (
                                        <span className="fireknife-booking__ticket-hint">
                                            {' '}
                                            ({t('widgetBookingSummary.pendingAdd')})
                                        </span>
                                    )}
                                    {ticket.isPendingForRemoving && (
                                        <span className="fireknife-booking__ticket-hint">
                                            {' '}
                                            ({t('widgetBookingSummary.pendingRemoved')})
                                        </span>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

export default ReservationTickets;
